import * as Popover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";

import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { normalizeSearch } from "@/shared/lib/search";
import {
  controlSizeClassName,
  inputClassName,
  listItemClassName,
  type ControlSize,
} from "@/shared/ui/styles";

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  /** Shown before the label, in the list and the trigger: an avatar, an icon. */
  leading?: ReactNode;
  /** More text the search matches, such as an email or a project key. */
  keywords?: readonly string[];
}

interface ComboboxProps {
  options: readonly ComboboxOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  /** Shown in the trigger when nothing is chosen. */
  placeholder: string;
  searchPlaceholder: string;
  /** Shown when the search matches nothing. */
  emptyLabel: string;
  /** Adds a "Clear selection" row, which sets the value to null. */
  allowClear?: boolean;
  disabled?: boolean;
  /** "sm" (h-8, the default) or "md" (h-9), as for Select. */
  size?: ControlSize;
  triggerClassName?: string;
  /** Pair with a Field's label, or name it with aria-label. */
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

const CLEAR_ITEM = "combobox:clear";

/** Typing on the closed trigger opens the list with that text already searched. */
function isTypedCharacter(event: KeyboardEvent): boolean {
  return (
    event.key.length === 1 && event.key !== " " && !event.ctrlKey && !event.metaKey && !event.altKey
  );
}

/**
 * Pick one option from a long list by typing: people, clients, projects. The search folds Arabic
 * spellings (normalizeSearch), so "احمد" finds "أحمد". Arrow keys move, Enter picks, Escape closes,
 * and focus returns to the trigger either way.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  allowClear = false,
  disabled,
  size = "sm",
  triggerClassName,
  ...aria
}: ComboboxProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = options.find((option) => option.value === value);

  const searchable = useMemo(
    () =>
      options.map((option) => ({
        option,
        text: normalizeSearch(
          [option.label, option.description ?? "", ...(option.keywords ?? [])].join(" "),
        ),
      })),
    [options],
  );
  const needle = normalizeSearch(search);
  const visible = needle
    ? searchable.filter((entry) => entry.text.includes(needle)).map((entry) => entry.option)
    : options;

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch("");
  };
  const choose = (next: string | null) => {
    onChange(next);
    changeOpen(false);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      changeOpen(true);
    } else if (isTypedCharacter(event)) {
      event.preventDefault();
      setSearch(event.key);
      setOpen(true);
    }
  };

  return (
    // Modal, so the list scrolls and takes focus even inside a dialog.
    <Popover.Root open={open} onOpenChange={changeOpen} modal>
      <Popover.Trigger
        role="combobox"
        aria-haspopup="listbox"
        disabled={disabled}
        onKeyDown={onTriggerKeyDown}
        className={cn(
          inputClassName,
          controlSizeClassName[size],
          "flex cursor-pointer items-center gap-2 pe-2 text-start data-[state=open]:border-brand data-[state=open]:ring-3 data-[state=open]:ring-brand-ring",
          triggerClassName,
        )}
        {...aria}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {selected ? (
            <>
              {selected.leading}
              <span className="user-text min-w-0 truncate text-ink">{selected.label}</span>
            </>
          ) : (
            <span className="truncate text-ink-subtle">{placeholder}</span>
          )}
        </span>
        <ChevronDown className="size-4 shrink-0 text-ink-faint" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="z-70 w-(--radix-popover-trigger-width) min-w-52 animate-pop-in overflow-hidden rounded-xl border border-line bg-surface shadow-overlay"
        >
          <Command
            shouldFilter={false}
            loop
            label={searchPlaceholder}
            defaultValue={value ?? undefined}
          >
            <div className="flex items-center gap-2 border-b border-line px-2.5">
              <Search className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder={searchPlaceholder}
                className="h-9 min-w-0 flex-1 bg-transparent text-[14px] text-ink outline-none placeholder:text-ink-subtle"
              />
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1">
              {visible.length === 0 ? (
                <p className="px-2 py-6 text-center text-[13.5px] text-ink-muted">{emptyLabel}</p>
              ) : null}
              {visible.map((option) => (
                <Command.Item
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    choose(option.value);
                  }}
                  className={listItemClassName}
                >
                  {option.leading}
                  <span className="min-w-0 flex-1">
                    <span className="user-text block truncate">{option.label}</span>
                    {option.description ? (
                      <span className="block truncate text-[12.5px] text-ink-subtle">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                  {option.value === value ? (
                    <Check className="size-4 shrink-0 text-brand" aria-hidden="true" />
                  ) : null}
                </Command.Item>
              ))}
              {allowClear && value !== null && needle === "" ? (
                <Command.Separator className="-mx-1 my-1 h-px bg-line" />
              ) : null}
              {allowClear && value !== null && needle === "" ? (
                <Command.Item
                  value={CLEAR_ITEM}
                  onSelect={() => {
                    choose(null);
                  }}
                  className={cn(listItemClassName, "text-ink-muted")}
                >
                  <X className="size-4 shrink-0" aria-hidden="true" />
                  {t("combobox.clear")}
                </Command.Item>
              ) : null}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
